"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import type { SidebarItemType } from "../_components/layout/sidebar/types";

interface SidebarGroupOrderState {
  [key: string]: string[]; // key format: "organizationId:sidebarItemType"
}

interface SidebarGroupOrderContextType {
  getSidebarGroupOrder: (
    organizationId: string,
    itemType?: SidebarItemType,
  ) => string[] | undefined;
  setSidebarGroupOrder: (
    organizationId: string,
    order: string[],
    itemType?: SidebarItemType,
  ) => void;
}

const SidebarGroupOrderContext = createContext<
  SidebarGroupOrderContextType | undefined
>(undefined);

export function SidebarGroupOrderProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [sidebarGroupOrders, setSidebarGroupOrders] =
    useState<SidebarGroupOrderState>({});

  // キー生成をメモ化
  const createKey = useCallback(
    (organizationId: string, itemType?: SidebarItemType) =>
      itemType ? `${organizationId}:${itemType}` : organizationId,
    [],
  );

  const getSidebarGroupOrder = useCallback(
    (organizationId: string, itemType?: SidebarItemType) => {
      const key = createKey(organizationId, itemType);
      return sidebarGroupOrders[key];
    },
    [sidebarGroupOrders, createKey],
  );

  const setSidebarGroupOrder = useCallback(
    (organizationId: string, order: string[], itemType?: SidebarItemType) => {
      const key = createKey(organizationId, itemType);
      setSidebarGroupOrders((prev) => {
        // 値が変わってない場合は更新しない（再レンダリング防止）
        if (prev[key] === order) return prev;

        return {
          ...prev,
          [key]: order,
        };
      });
    },
    [createKey],
  );

  return (
    <SidebarGroupOrderContext.Provider
      value={{ getSidebarGroupOrder, setSidebarGroupOrder }}
    >
      {children}
    </SidebarGroupOrderContext.Provider>
  );
}

export function useSidebarGroupOrder() {
  const context = useContext(SidebarGroupOrderContext);
  if (!context) {
    throw new Error(
      "useSidebarGroupOrder must be used within a SidebarGroupOrderProvider",
    );
  }
  return context;
}
